<template>
  <validation-observer ref="form" class="mb-3" tag="div">
    <div class="row">
      <div class="col-md-6">
        <validation-provider
            v-slot="{ validated, errors }"
            :name="lodash.startCase($t('purchase_payments.ticket_id'))"
            rules="required"
            vid="ticket_id"
        >
          <b-form-group
              :invalid-feedback="errors[0]"
              :label="`${lodash.startCase($t('purchase_payments.ticket_id'))}`"
              :state="errors[0] ? false : validated ? true : null"
              class="row"
              label-cols-md="12"
          >
            <multiselect-ajax
                :key="`${_uid}-tickets`"
                v-model.number="model.ticketable.ticket_id"
                :custom-label="
                ({ id, customer_type, created_at, creator: { name: creator_name } = {} }) =>
                  `${id}: ${$t(`tickets.customer_types.${customer_type}`)}, ${$t('created_by_{name}_on_{date}', {
                    name: creator_name,
                    date: $moment
                      .utc(created_at)
                      .local()
                      .format('DD MMMM YYYY')
                  })}`
              "
                :disabled="$route.params.key"
                :modify-response="
                response => {
                  const {
                    data: { data }
                  } = response;
                  if (data.length) {
                    const dataEdited = data.map(datum => {
                      const isAllTripsAreUnloaded = datum.trips.every(trip => trip.sale_orders.length > 0);
                      return {
                        ...datum,
                        $isDisabled:
                          datum.status === 'close' || datum.purchase_orders.length === 0 || isAllTripsAreUnloaded
                      };
                    });
                    const dataEnabled = dataEdited.filter(datum => !datum.$isDisabled);
                    const dataDisabled = dataEdited.filter(datum => datum.$isDisabled);
                    response.data.data = [...dataEnabled, ...dataDisabled];
                  }
                  return response;
                }
              "
                :params="{
                with: 'creator,purchase_orders,trips.trip_items.purchase_item,vehicle,trips.sale_orders',
                customer_type: 'lcs',
                where_has: 'trips',
                sort: 'id,desc'
              }"
                name="ticket_id"
                url="/tickets"
                @update:selected="onTicketUpdated"
            >
            </multiselect-ajax>
          </b-form-group>
        </validation-provider>
      </div>
      <div class="col-md-6">
        <validation-provider v-slot="{ validated, errors }" name="ID nota armada" rules="required" vid="trip_id">
          <b-form-group
              :invalid-feedback="errors[0]"
              :label="`ID Nota Armada` + '*'"
              :state="errors[0] ? false : validated ? true : null"
              class="row"
              label-cols-md="12"
          >
            <multiselect-ajax
                :key="`${_uid}-trip-${model.ticketable.ticket_id}`"
                v-model.number="model.trip_id"
                :custom-label="m => m.id"
                :disabled="$route.params.key || !model.ticketable.ticket_id"
                :modify-response="
                response => {
                  const {
                    data: { data }
                  } = response;
                  if (data.length) {
                    const dataEdited = data.map(datum => ({
                      ...datum,
                      $isDisabled: datum.sale_orders.length > 0
                    }));
                    const dataEnabled = dataEdited.filter(datum => !datum.$isDisabled);
                    const dataDisabled = dataEdited.filter(datum => datum.$isDisabled);
                    response.data.data = [...dataEnabled, ...dataDisabled];
                  }
                  return response;
                }
              "
                :params="{
                ticket_id: model.ticketable.ticket_id,
                with:
                  'vehicle,driver,trip_items.purchase_item.purchase_order.product,customer,sale_orders,delivery_order',
                sort: 'id,desc'
              }"
                :url="`/trips`"
                name="ID nota armada"
                @update:selected="onTripSelected"
            >
            </multiselect-ajax>
          </b-form-group>
        </validation-provider>
      </div>
      <template v-if="model.ticketable.ticket_id && model.trip_id">
        <div class="col-md-6">
          <b-form-group :label="`Pabrik`" class="row" label-cols-md="12">
            <b-form-input :value="processingName" disabled name="processing" type="text"></b-form-input>
          </b-form-group>
        </div>
        <div class="col-md-6">
          <b-form-group :label="`Supir`" class="row" label-cols-md="12">
            <b-form-input :value="driverName" disabled name="driver" type="text"></b-form-input>
          </b-form-group>
        </div>
        <div class="col-md-6">
          <b-form-group :label="`Kendaraan`" class="row" label-cols-md="12">
            <b-form-input :value="vehicleName" disabled name="vehicle" type="text"></b-form-input>
          </b-form-group>
        </div>
        <validation-provider
            v-slot="{ validated, errors }"
            :name="lodash.startCase($t('unloads.fibers'))"
            :vid="`unloads.${key}.fibers`"
            class="col-md-6"
            rules="gt:0|integer|required"
        >
          <b-form-group
              :invalid-feedback="errors[0]"
              :label="`${lodash.startCase($t('unloads.fibers'))}*`"
              :state="errors[0] ? false : validated ? true : null"
              class="row"
              label-cols="12"
              label-cols-md="12"
              label-cols-xl="12"
          >
            <b-input-group>
              <numeral-input
                  id="fibers"
                  v-model="model.box"
                  :name="`unloads[fibers]`"
                  :placeholder="`${lodash.startCase($t('unloads.fibers'))}`"
                  :state="errors[0] ? false : validated ? true : null"
                  style="text-align: right"
              />
            </b-input-group>
          </b-form-group>
        </validation-provider>
        <div class="col-md-6">
          <validation-provider
              v-slot="{ validated, errors }"
              :rules="`required|after_or_equal:${latestPurchaseOrder.ordered_at}`"
              name="Tanggal Nota Timbang"
              vid="ordered_at"
          >
            <b-form-group
                :invalid-feedback="errors[0]"
                :label="`${lodash.upperFirst($t('weight_notes.date'))} ${lodash.startCase($t('weight_notes.singular'))}*`"
                :state="errors[0] ? false : validated ? true : null"
                class="row"
                label-cols-md="12"
            >
              <date-picker
                  v-model="model.ordered_at"
                  :state="errors[0] ? false : validated ? true : null"
                  format="DD MMMM YYYY"
                  name="ordered_at"
                  type="date"
                  @input="model.ordered_at = $event"
              ></date-picker>
            </b-form-group>
          </validation-provider>
        </div>
        <validation-provider
            v-slot="{ validated, errors }"
            :name="lodash.upperFirst($t('purchase_orders.file'))"
            :rules="model.id ? '' : 'required'"
            vid="files.0"
        >
          <b-form-group
              :invalid-feedback="errors[0]"
              :state="errors[0] ? false : validated ? true : null"
              class="row"
              label="Attachment"
              label-class="attachment"
              label-cols-md="12"
          >
            <div class="row">
              <div class="col-md-6">
                <file-drag-and-drop
                    v-model="model.files"
                    :drop-placeholder="lodash.upperFirst($t('drop file here'))"
                    :placeholder="lodash.upperFirst($t('choose a file or drop it here'))"
                    :state="errors[0] ? false : validated ? true : null"
                    accept="image/*"
                    name="`files`"
                ></file-drag-and-drop>
              </div>
              <div class="col-md-12">
                <div class="d-flex flex-wrap gap-2">
                  <attachment-v2 :files="model.files">
                    <template v-slot:tool-button="{file}">
                      <!--                      <b-button class="attachment-button" variant="light" @click="handleFileDownload">-->
                      <!--                        <i class="bx bx-download"></i>-->
                      <!--                      </b-button>-->
                      <a :href="file.url" target="_blank">
                        <b-button class="attachment-button" variant="light">
                          <i class="bx bx-show"></i>
                        </b-button>
                      </a>
                      <b-button class="attachment-button" variant="light" @click="handleFileDelete">
                        <i class="bx bx-trash"></i>
                      </b-button>
                    </template>
                  </attachment-v2>
                </div>
              </div>
            </div>
          </b-form-group>
        </validation-provider>
        <hr class="mt-3"/>
        <b-form-group
            :label="`${$t('item')} ${$t('weight_notes.singular')}`"
            class="row"
            label-class="section-header"
            label-cols-md="12"
            style="overflow-x: auto"
        >
          <div class="container">
            <template v-for="(trip_item, key) in model.trip_items">
              <div v-if="trip_item._action !== 'delete'" :key="`trip_items.key.${key}`">
                <div :key="`trip_items.key.${key}`" :class="`row`">
                  <div class="col-1 sticky-item-key">
                    <b-form-group :label="key === 0 ? 'No' : ''">
                      <b-input-group>
                    <span class="py-2">
                      {{ key + 1 }}
                    </span>
                      </b-input-group>
                    </b-form-group>
                  </div>
                  <div class="col-5 col-md-9 row flex-nowrap align-items-start h-100">
                    <div class="col-md-3">
                      <b-form-group :label="key === 0 ? lodash.upperFirst($t('products.singular')) : ''">
                        <b-input-group>
                      <span class="py-2">
                        {{ trip_item.purchase_item.purchase_order.product.name }}
                      </span>
                        </b-input-group>
                      </b-form-group>
                    </div>

                    <div class="col-md-5 row h-100">
                      <div
                          v-if="key === 0"
                          class="col-md-12 text-center text-block text-sm text-semibold border-start"
                          style="margin-top: -20px;"
                      >
                        Nota Armada
                      </div>
                      <div
                          :style="`min-height: ${110 * undeletedChildItems('trip_items', key, 'sale_items').length}px; `"
                          class="col-md-6 border-start"
                      >
                        <b-form-group
                            :label="key === 0 ? `Size` : ''"
                            label-class="text-block text-sm text-semibold text-blue-600"
                        >
                          <b-input-group>
                            <span class="text-blue-600 py-2">{{ $numeral(trip_item.size).format("0,0.[00]") }}</span>
                          </b-input-group>
                        </b-form-group>
                      </div>
                      <validation-provider :style="`min-height: ${110 * undeleted}px; `" class="col-md-6">
                        <b-form-group
                            :label="key === 0 ? `Biomassa (kg)` : ''"
                            label-class="text-block text-sm text-semibold text-blue-600"
                        >
                          <b-input-group>
                        <span class="text-blue-600 py-2">{{
                            $numeral(trip_item.quantity).format("0,0.[00]")
                          }}</span>
                          </b-input-group>
                        </b-form-group>
                      </validation-provider>
                    </div>
                    <!-- unload item repeater -->
                    <div class="col-md-12 row">
                      <template v-for="(sale_item, child_key) in undeletedChildItems('trip_items', key, 'sale_items')">
                        <div :key="child_key" class="col-md-12 row flex-nowrap align-items-start h-100">
                          <div class="col-md-6 row h-100">
                            <div
                                v-if="key === 0 && child_key == 0"
                                class="col-md-12 text-center text-block text-sm text-semibold border-start"
                                style="margin-top: -20px;"
                            >
                              Bongkar
                            </div>
                            <validation-provider
                                v-slot="{ validated, errors }"
                                :name="lodash.upperFirst($t('purchase_orders.size'))"
                                :vid="`trip_items.${key}.${child_key}size`"
                                class="col-md-6 border-start border-start"
                                rules="required|min_value:1"
                                style="min-height: 110px;"
                            >
                              <b-form-group
                                  :invalid-feedback="errors[0]"
                                  :label="key === 0 && child_key == 0 ? `Size*` : ''"
                                  :state="errors[0] ? false : validated ? true : null"
                              >
                                <b-input-group>
                                  <numeral-input
                                      v-model="sale_item['size']"
                                      :name="`trip_items[${key}][${child_key}][size]`"
                                      :placeholder="`${$t('Estimation')} ${round(trip_item.size * 0.9, 2)}`"
                                      :state="errors[0] ? false : validated ? true : null"
                                      format="0,0.[00]"
                                      style="text-align: right"
                                  />
                                </b-input-group>
                              </b-form-group>
                            </validation-provider>
                            <validation-provider
                                v-slot="{ validated, errors }"
                                :name="lodash.upperFirst($t('purchase_orders.biomassa'))"
                                :vid="`trip_items.${key}.${child_key}.quantity`"
                                class="col-md-6 border-end"
                                rules="required|gt:0"
                                style="min-height: 110px; "
                            >
                              <b-form-group
                                  :invalid-feedback="errors[0]"
                                  :label="key === 0 && child_key == 0 ? `Biomassa (kg)*` : ''"
                                  :state="errors[0] ? false : validated ? true : null"
                              >
                                <b-input-group>
                                  <numeral-input
                                      id="quantity"
                                      v-model="sale_item['quantity']"
                                      :name="`trip_items[${key}][${child_key}][quantity]`"
                                      :placeholder="`${$t('Estimation')} ${round(trip_item.quantity * 1.12, 2)}`"
                                      :state="errors[0] ? false : validated ? true : null"
                                      format="0,0.[00]"
                                      lazy-formatter
                                      style="text-align: right"
                                      @blur="
                                  scrollTo(`remove_item_${key}_${child_key}`);
                                  onBlurSaleItemQuantity(sale_item);
                                "
                                  />
                                </b-input-group>
                                <div class="float-end d-flex">
                                  <b-button
                                      v-if="undeletedChildItems('trip_items', key, 'sale_items').length > 1"
                                      class="text-danger"
                                      size="sm"
                                      style="margin-right: 10px"
                                      tag="span"
                                      variant="link"
                                      @click="removeChildItem('trip_items', key, 'sale_items', child_key)"
                                  >{{ $t("Delete") }}
                                  </b-button
                                  >
                                  <b-button
                                      v-if="child_key === undeletedChildItems('trip_items', key, 'sale_items').length - 1"
                                      size="sm"
                                      tag="span"
                                      variant="link"
                                      @click="addChildItem('trip_items', key, 'sale_items')"
                                  >
                                    {{ $t("Add new {something}", {something: ""}) }}
                                  </b-button>
                                </div>
                              </b-form-group>
                            </validation-provider>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </b-form-group>
        <div class="row">
          <div :key="`purchase-order-total-initial-quantity-${totalQuantity}`" class="col-md-4 text-center">
            {{ lodash.startCase($t("unloads.purchase_order_total_biomassa")) }}:
            {{ $numeral(totalQuantity).format("0,0.[00]") }} {{ lodash.startCase($t("units.kg")) }}
          </div>
          <div :key="`delivery-order-total-quantity-${estimatedTotalQuantity}`" class="col-md-4 text-center">
            {{ lodash.startCase($t("unloads.estimated_quantity_total")) }}:
            {{ $numeral(estimatedTotalQuantity).format("0,0.[00]") }} {{ lodash.startCase($t("units.kg")) }}
          </div>
          <div :key="`purchase-order-total-quantity-${actualTotalQuantity}`" class="col-md-4 text-center">
            {{ lodash.startCase($t("unloads.actual_quantity_total")) }}:
            {{ $numeral(actualTotalQuantity).format("0,0.[00]") }} {{ lodash.startCase($t("units.kg")) }}
          </div>
        </div>
        <hr/>
      </template>

    </div>
  </validation-observer>
</template>

<script>
import AttachmentV2 from "@/router/views/trips/components/attachment-v2.vue";
import FormModel from "./model";
import FileDragAndDrop from "@/components/inputs/file-input";
import {round} from "lodash";

export default {
  extends: FormModel,
  components: {
    AttachmentV2,
    FileDragAndDrop
  },
  data() {
    return {
      backupFile: {
        files: !this.lodash.isEmpty(this.value.files) ? this.value.files : {name: "files"}
      },
      file_accepts: ["image/jpeg", "image/jpg", "image/png", "application/pdf"],
      dueAt: null,
      ...this.value,
      firstFile: null,
      selectedCustomerType: null,
      quality_options: [
        {value: "fresh", text: "Fresh"},
        {value: "km", text: "Kulit Muda (KM)"},
        {value: "bs", text: "Below Standard (BS)"},
        {value: "us", text: "Under Size (US)"},
        {value: "merah", text: "Merah"}
      ],
      fund_options: [
        {value: "fresh", text: "Fresh"},
        {value: "kmtb", text: "KMTB"}
      ],
      preview: "",
      model: {
        is_draft: true,
        total: 0,
        ...this.value,
        unit_duration: "days",
        files: [...((this.value.files || []).length ? this.value.files : [])],
        purchase_items: [...((this.value.purchase_items || []).length ? this.value.purchase_items : [{}])],
        sale_items: [
          ...((this.value.sale_items || []).length
              ? this.value.sale_items
              : [
                {
                  quality: "fresh"
                }
              ])
        ],
        actual_sale_items: [...((this.value.actual_sale_items || []).length ? this.value.actual_sale_items : [])],
        ticket: !this.lodash.isEmpty(this.value.ticket) ? this.value.ticket : {},
        ticketable: !this.lodash.isEmpty(this.value.ticketable) ? this.value.ticketable : {ticket_id: null},
        paymentable: !this.lodash.isEmpty(this.value.paymentable)
            ? this.value.paymentable
            : {id: null, ticketable: {ticket: {}}},
        trip_items: [],
        biomassa_incremental_factor: 1.12,
        size_incremental_factor: 0.9,
        sale_paid_by_jala: [
          ...((this.value.sale_paid_by_jala || []).length
              ? this.value.sale_paid_by_jala
              : [
                {name: "Diskon", total: 0, disabled: true, paid_by: "jala"},
                {name: "Biaya Percepatan", total: 0, disabled: true, paid_by: "jala"}
              ])
        ],
      }
    };
  },
  mounted() {
    const params = this.$route.params;
    this.$set(this, "model", {
      ...this.model,
      ...params
    });
    // this.checkLocalStorage()
  },
  watch: {
    'model.trip': function (val) {
      this.$nextTick(() => {
        this.model.type = val?.type

      })
    },
    "model.ticketable.ticket_id": function () {
      this.$set(this.model, "trip", null);
      this.$set(this.model, "trip_items", []);
    },
    model: {
      deep: true,
      handler: function () {
        let dueAt = null;
        if (this.model.ordered_at) {
          dueAt = this.$moment(this.model.ordered_at)
              .add(this.model.duration || 0, "days")
              .format("YYYY-MM-DD");
        }
        this.dueAt = dueAt;
      }
    }
  },
  computed: {
    dueDate() {
      if (!this.model.ordered_at) return "-";
      let dueDate = this.$moment(this.model.ordered_at, "YYYY-MM-DD").add(
          this.model.duration > 0 ? this.model.duration : 0,
          this.model.unit_duration
      );
      return dueDate.format("D MMMM YYYY");
    },
    isJalaHarvest() {
      let tripItem = this.model.trip_items[0]
      return tripItem?.purchase_item?.purchase_order?.type === 'harvest'
    },
    customerName() {
      return "-";
      // return (
      //   this.model?.sale_items[0]?.purchase_item?.purchase_order?.customer
      //     ?.name || "-"
      // );
    },
    vehicleName() {
      let vehicle = this.model?.trip?.vehicle;
      let vehicleName = vehicle?.name;

      if (vehicle?.type === "heavy") vehicleName = this.$t("vehicles.truck");
      else if (vehicle?.type === "medium") vehicleName = this.$t("vehicles.crank_truck");
      else if (vehicle?.type === "light") vehicleName = this.$t("vehicles.pickup");

      return vehicle ? `${this.lodash.upperFirst(vehicleName)} - ${vehicle?.license_plate}` : "";
    },
    driverName() {
      return this.model?.trip?.driver?.name
          ? `${this.model?.trip?.driver?.name} - ${this.model?.trip?.driver?.phone_number}`
          : "";
    },
    processingName() {
      return this.model?.trip?.customer ? `${this.model.trip.customer.name} (${this.model.trip.customer.code})` : "";
    },
    actualTotalPrice() {
      let saleItemTotal = 0;
      this.model.trip_items.forEach((_, index) => {
        saleItemTotal += this.undeletedChildItems("trip_items", index, "sale_items").reduce((acc, item) => {
          acc += (item.quantity || 0) * (item.unit_price || 0);
          return acc;
        }, 0);
      });
      return saleItemTotal;
    },
    actualTotalQuantity() {
      let total = 0;
      this.model.trip_items.forEach(trip_item => {
        total += trip_item.sale_items.reduce((acc, item) => {
          return acc + item.quantity || 0;
        }, 0);
      });
      return total;
    },
    estimatedTotalQuantity() {
      const estimated = this.model.trip_items.reduce((acc, item) => {
        return acc + item.quantity * 1.12;
      }, 0);
      return Math.round(estimated * 100) / 100;
    },
    totalQuantity() {
      const total = this.model.trip_items.reduce((acc, item) => {
        return acc + item.quantity;
      }, 0);
      return total;
    },
    saleOrderTotalPrice() {
      return this.actualTotalPrice;
    },
    purchaseOrderTotalTonase() {
      return [...this.undeletedItems("sale_items"), ...this.undeletedItems("actual_sale_items")].reduce(
          (total, v) => total + (v.quantity || 0),
          0
      );
    },
    latestPurchaseOrder() {
      const tripItems = this.model.trip_items;
      const latest =
          tripItems.sort((a, b) => {
            return (
                new Date(b.purchase_item.purchase_order.ordered_at) - new Date(a.purchase_item.purchase_order.ordered_at)
            );
          })?.[0] || null;
      if (latest) {
        return latest.purchase_item.purchase_order;
      }
      return {};
    },
    saleItemTotal() {
      let total = this.model.trip_items
          .filter(tripItem => tripItem._action !== "delete")
          .reduce((total, tripItem) => {
            let saleItemTotal = tripItem.sale_items
                .filter(saleItem => saleItem._action !== "delete")
                .reduce((total, saleItem) => total + (saleItem.total_price || 0), 0);
            return total + (saleItemTotal || 0);
          }, 0);
      return total;
    },

  },
  methods: {
    round,
    handleFileDownload() {

    },
    handleFileDelete(event) {
      this.model.files.splice(event.index, 1)
    },
    onDeleteFile(file) {
      this.$set(this.model, file, {
        name: file,
        _action: "delete"
      });
    },
    removeFile(key, fileIndex) {
      const filteredFile = this.model?.[key]
          .map((file, index) => {
            if (index == fileIndex && file.id) {
              file._action = "delete";
            }
            return file;
          })
          .filter((file, index) => {
            if (file._action === "delete") {
              return true;
            }
            return index !== fileIndex;
          });
      this.$set(this.model, key, filteredFile);
    },
    onTicketUpdated(payload) {
      if (payload) {
        this.model.ticket = payload;
      } else {
        const params = this.$route.params;
        this.$set(this.model, "ticketable", {
          ticket_id: params?.ticketable?.ticket_id
        });
        if (!this.model.trip_id) {
          this.$set(this, "model", {
            trip_id: params?.trip_id
          });
        }
      }
    },
    scrollTo(id) {
      const element = this.$el.querySelector(`#${id}`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({behavior: "smooth", block: "center"});
        }, 100);
      }
    },
    onPurchaseOrderSelected(payload) {
      this.$set(this.model, "paymentable", {
        ...this.model.paymentable,
        ...payload
      });
      this.$set(this.model, "purchase_order_id", `${payload.map(p => p.id).toString()},`);
      this.$set(this.model, "purchase_order_selected", payload);
    },
    onPurchaseItemSelected(payload) {
      this.model.trip_items = payload;
      this.model.difference = this.differenceBiomass();
    },
    onTripSelected(payload) {
      if (!payload) {
        this.model.customer_id = null;
        this.model.trip_items = []
        this.model.sale_paid_by_jala = []
        this.model.sale_paid_by_partner = []
        return;
      }
      payload.trip_items = payload.trip_items.map(item => {
        const saleItems = this.model.sale_items.filter(saleItem => saleItem.trip_item_id === item.id);
        if (saleItems.length == 0) {
          saleItems.push({
            quality: "fresh"
          });
        }
        return {
          ...item,
          sale_items: saleItems
        };
      });
      this.model.customer_id = payload.customer_id;
      this.model.trip_items = payload.trip_items;
      this.$set(this.model, "trip", payload);
      this.$set(this.model, "trip_items", payload.trip_items);
      if (!this.model.id) {
        this.$set(this.model, "box", parseInt(payload.delivery_order.delivery_item_total_packing));
      }
    },
    checkLocalStorage() {
      let local = localStorage.getItem("purchase_order");
      if (local && !this.model.id) {
        let jsonData = JSON.parse(local);
        let vm = this;
        this.$set(this, "model", {
          ...jsonData,
          files: jsonData.files.map(function (item) {
            if (Object.keys(item).length)
              return {
                ...item,
                file: vm.dataURLtoFile(item.preview, item.name)
              };
            else return {...item};
          })
        });
      }
    },
    addItem(key, item = {}) {
      this.model[key].push(item);
    },
    addChildItem(key, index, childKey) {
      const parent = this.model[key][index];
      this.model[key][index][childKey].push({
        quality: "fresh",
        product_id: parent.product_id
      });
    },
    undeletedItems(key) {
      return (this.model[key] || []).filter(r => r._action !== "delete");
    },
    undeletedChildItems(key, index, childKey) {
      return this.model[key][index][childKey].filter(r => r._action !== "delete");
    },
    removeItem(key, index) {
      const items = this.undeletedItems(key);
      if ((items[index] || {}).id) {
        items[index]._action = "delete";
      } else {
        items.splice(index, 1);
      }
      this.model[key] = items;
    },
    removeChildItem(key, index, childKey, childIndex) {
      const items = this.undeletedItems(key);
      if ((items[index][childKey][childIndex] || {}).id) {
        items[index][childKey][childIndex]._action = "delete";
      } else {
        items[index][childKey].splice(childIndex, 1);
      }
      this.model[key] = items;
    },
    dataURLtoFile(dataUrl, filename) {
      let arr = dataUrl.split(","),
          mime = arr[0].match(/:(.*?);/)[1],
          bstr = atob(arr[1]),
          n = bstr.length,
          u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, {type: mime});
    },
    onBlurSaleItemQuantity(saleItem) {
      this.setSaleItemTotalPrice(saleItem);
      this.setSaleOrderTotal();
    },
    onBlurSaleItemUnitPrice(saleItem) {
      this.setSaleItemTotalPrice(saleItem);
      this.setSaleOrderTotal();
    },

    setSaleItemTotalPrice(saleItem) {
      this.$set(saleItem, "total_price", (saleItem.quantity || 0) * (saleItem.unit_price || 0));
    },
    setSaleOrderTotal() {
      this.$set(this.model, "total", this.saleItemTotal);
    },
  }
};
</script>

<style lang="scss" scoped>
@import "~@/design/app";

::v-deep {
  .container {
    margin: 0;
    width: 100%;
  }

  .item-section {
    .item-title {
      color: #6C7589;
      font: 800 12px/20px Lato;
      text-transform: uppercase;
    }

    .item-body {
      color: #000;
      font: 600 12px/14px Inter;
    }
  }

  .col-form-label {
    @extend .text-sm;
    @extend .text-block;
    @extend .text-semibold;
    @extend .text-neutral-default;

    &.section-header {
      @extend .text-neutral-300;
      color: #6C7589 !important;
      font: 800 12px/20px Lato;
      text-transform: uppercase;
    }

    @each $color-name, $color-value in $colors {
      &.text-#{$color-name} {
        @extend .text-#{$color-name};
      }
    }
  }

  .attachment-button {
    border-radius: 4px;
    padding: 1px;
    font-size: 12px;
    width: 20px;
    height: 20px;
  }
}
</style>
